import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './FoodTracker.module.css'

interface FoodExposure { type: string; date: string }
interface TrackedFood { id: string; name: string; category: string; exposures: FoodExposure[]; dateAdded: string }

const STORAGE_KEY = 'mellomap-food-tracker'
const CATEGORIES = [
  { key: 'vegetable', label: 'Vegetables', emoji: '' },
  { key: 'fruit', label: 'Fruits', emoji: '' },
  { key: 'grains', label: 'Grains/Roti', emoji: '' },
  { key: 'dal', label: 'Dal/Lentils', emoji: '' },
  { key: 'dairy', label: 'Dairy/Paneer', emoji: '' },
  { key: 'nonveg', label: 'Non-veg', emoji: '' },
  { key: 'snacks', label: 'Snacks', emoji: '' },
]
const EXPOSURE_TYPES = [
  { key: 'looked', emoji: '', label: 'Looked' },
  { key: 'touched', emoji: '', label: 'Touched' },
  { key: 'smelled', emoji: '', label: 'Smelled' },
  { key: 'licked', emoji: '', label: 'Licked' },
  { key: 'bit', emoji: '', label: 'Bit' },
  { key: 'ate', emoji: '', label: 'Ate' },
]
const TARGET = 20

function load(): TrackedFood[] { const d = localStorage.getItem(STORAGE_KEY); return d ? JSON.parse(d) : [] }
function save(data: TrackedFood[]) { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)) }

export default function FoodTracker() {
  const navigate = useNavigate()
  const [foods, setFoods] = useState<TrackedFood[]>(load)
  const [name, setName] = useState('')
  const [category, setCategory] = useState('vegetable')
  const [filter, setFilter] = useState('all')

  const addFood = () => {
    if (!name.trim()) return
    const food: TrackedFood = { id: Date.now().toString(), name: name.trim(), category, exposures: [], dateAdded: new Date().toISOString() }
    const updated = [food, ...foods]
    setFoods(updated); save(updated); setName('')
  }

  const addExposure = (foodId: string, type: string) => {
    const updated = foods.map(f => f.id === foodId ? { ...f, exposures: [...f.exposures, { type, date: new Date().toISOString() }] } : f)
    setFoods(updated); save(updated)
  }

  const removeFood = (id: string) => {
    const updated = foods.filter(f => f.id !== id)
    setFoods(updated); save(updated)
  }

  const filtered = foods.filter(f => {
    if (filter === 'all') return true
    if (filter === 'accepted') return f.exposures.length >= TARGET
    if (filter === 'progress') return f.exposures.length > 0 && f.exposures.length < TARGET
    if (filter === 'new') return f.exposures.length === 0
    return true
  })

  const accepted = foods.filter(f => f.exposures.length >= TARGET).length
  const thisWeek = foods.reduce((sum, f) => {
    const weekAgo = Date.now() - 7 * 86400000
    return sum + f.exposures.filter(e => new Date(e.date).getTime() > weekAgo).length
  }, 0)

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <h1 className={styles.title}>Food Tracker</h1>
      </header>

      <div className={styles.explainer}>Children need 15-20 positive exposures to a new food before they may accept it. Every interaction counts — even just looking at it!</div>

      <div className={styles.stats}>
        <div className={styles.statCard}><strong>{foods.length}</strong><span>Tracking</span></div>
        <div className={styles.statCard}><strong>{accepted}</strong><span>Accepted</span></div>
        <div className={styles.statCard}><strong>{thisWeek}</strong><span>This week</span></div>
      </div>

      <div className={styles.addRow}>
        <input className={styles.addInput} value={name} onChange={e => setName(e.target.value)} placeholder="Food name..." onKeyDown={e => e.key === 'Enter' && addFood()} />
        <select className={styles.addSelect} value={category} onChange={e => setCategory(e.target.value)}>
          {CATEGORIES.map(c =><option key={c.key} value={c.key}>{c.emoji} {c.label}</option>)}
        </select>
        <button className={styles.addBtn} onClick={addFood} disabled={!name.trim()}>Add</button>
      </div>

      <div className={styles.filters}>
        {['all', 'progress', 'accepted', 'new'].map(f => (
          <button key={f} className={`${styles.filterPill} ${filter === f ? styles.filterActive : ''}`} onClick={() => setFilter(f)}>
            {f === 'all' ? 'All' : f === 'progress' ? 'In Progress' : f === 'accepted' ? 'Accepted' : 'New'}
          </button>
        ))}
      </div>

      <div className={styles.foodList}>
        {filtered.length === 0 && <p className={styles.empty}>{foods.length === 0 ? 'Start tracking foods your child is learning to eat.' : 'No foods in this category.'}</p>}
        {filtered.map(food => {
          const catInfo = CATEGORIES.find(c => c.key === food.category)
          const count = food.exposures.length
          const isAccepted = count >= TARGET
          return (
            <div key={food.id} className={`${styles.foodCard} ${isAccepted ? styles.foodAccepted : ''}`}>
              <div className={styles.foodHeader}>
                <span className={styles.foodEmoji}>{catInfo?.emoji}</span>
                <span className={styles.foodName}>{food.name}</span>
                {isAccepted && <span className={styles.acceptedBadge}>Accepted!</span>}
                <button className={styles.removeBtn} onClick={() => removeFood(food.id)}>✕</button>
              </div>
              <div className={styles.progressRow}>
                <div className={styles.progressBar}><div className={styles.progressFill} style={{ width: `${Math.min(100, (count / TARGET) * 100)}%` }} /></div>
                <span className={styles.progressLabel}>{count}/{TARGET}</span>
              </div>
              <div className={styles.exposureRow}>
                {EXPOSURE_TYPES.map(et => {
                  const typeCount = food.exposures.filter(e => e.type === et.key).length
                  return (
                    <button key={et.key} className={styles.exposureBtn} onClick={() => addExposure(food.id, et.key)} title={et.label}>
                      <span>{et.emoji}</span>
                      {typeCount > 0 && <span className={styles.exposureCount}>{typeCount}</span>}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
